<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { VISIT_DETAIL_TEXT } from '../utils/visitDetailPresenter';

const props = defineProps({
  show: { type: Boolean, default: false },
  mode: {
    type: String,
    default: 'talk',
    validator: value =>
      ['talk', 'best-practices', 'reschedule'].includes(value),
  },
  tenantName: { type: String, default: 'morador' },
  tenantPhone: { type: String, default: '' },
  businessContext: { type: String, default: 'for_rent' },
});

const emit = defineEmits(['close', 'whatsapp', 'call', 'continue-reschedule']);

const isBestPractices = computed(() => props.mode === 'best-practices');
const isReschedule = computed(() => props.mode === 'reschedule');
const isSale = computed(() => props.businessContext === 'for_sale');

const title = computed(() => {
  if (isBestPractices.value) {
    return VISIT_DETAIL_TEXT.TENANT_BEST_PRACTICES_TITLE;
  }
  if (isReschedule.value) {
    return VISIT_DETAIL_TEXT.RESCHEDULE_WITH_TENANT_TITLE;
  }
  return VISIT_DETAIL_TEXT.TENANT_DIALOG_TITLE;
});

const description = computed(() => {
  if (isBestPractices.value) {
    return VISIT_DETAIL_TEXT.TENANT_BEST_PRACTICES_DESCRIPTION;
  }
  if (isReschedule.value) {
    return VISIT_DETAIL_TEXT.RESCHEDULE_WITH_TENANT_DESCRIPTION;
  }
  return VISIT_DETAIL_TEXT.TENANT_DIALOG_DESCRIPTION;
});

const tips = computed(() => {
  const base = [
    {
      title: VISIT_DETAIL_TEXT.TENANT_DIALOG_FIRST_TIP_TITLE,
      description: VISIT_DETAIL_TEXT.TENANT_DIALOG_FIRST_TIP,
    },
  ];

  if (isBestPractices.value) {
    base.push({
      title: VISIT_DETAIL_TEXT.TENANT_BEST_PRACTICES_ARRIVAL_TITLE,
      description: VISIT_DETAIL_TEXT.TENANT_BEST_PRACTICES_ARRIVAL,
    });

    if (isSale.value) {
      base.push({
        title: VISIT_DETAIL_TEXT.TENANT_BEST_PRACTICES_INVESTOR_TITLE,
        description: VISIT_DETAIL_TEXT.TENANT_BEST_PRACTICES_INVESTOR,
      });
    }

    return base;
  }

  return [
    ...base,
    {
      title: VISIT_DETAIL_TEXT.TENANT_DIALOG_SECOND_TIP_TITLE,
      description: VISIT_DETAIL_TEXT.TENANT_DIALOG_SECOND_TIP,
    },
  ];
});

const callLabel = computed(() =>
  VISIT_DETAIL_TEXT.CALL_TENANT.replace('{tenantName}', props.tenantName)
);
</script>

<template>
  <LaisModal
    :show="show"
    :title="title"
    max-width-class="max-w-[520px]"
    full-screen
    @close="emit('close')"
  >
    <div class="grid gap-4">
      <p class="text-[14px] leading-relaxed text-lais-text-body">
        {{ description }}
      </p>

      <ul class="grid gap-3">
        <li
          v-for="tip in tips"
          :key="tip.title"
          class="grid grid-cols-[32px_1fr] gap-3 rounded-lais-sm border border-lais-border bg-lais-bg-soft px-3 py-3"
        >
          <span
            class="grid size-8 place-items-center rounded-full bg-white text-lais-primary"
          >
            <span class="i-lucide-check size-4" aria-hidden="true" />
          </span>
          <span class="min-w-0">
            <strong class="block text-[13px] font-semibold text-lais-text">
              {{ tip.title }}
            </strong>
            <span
              class="mt-1 block text-[13px] leading-relaxed text-lais-text-body"
            >
              {{ tip.description }}
            </span>
          </span>
        </li>
      </ul>
    </div>

    <template #footer>
      <div class="grid gap-2 sm:grid-cols-2">
        <LaisPrimaryButton
          v-if="!isBestPractices"
          icon="i-lucide-message-circle"
          :disabled="!tenantPhone"
          @click="emit('whatsapp')"
        >
          {{ VISIT_DETAIL_TEXT.WHATSAPP_TENANT }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="!isBestPractices"
          variant="ghost"
          icon="i-lucide-phone"
          :disabled="!tenantPhone"
          @click="emit('call')"
        >
          {{ callLabel }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="isReschedule"
          class="sm:col-span-2"
          icon="i-lucide-calendar-clock"
          @click="emit('continue-reschedule')"
        >
          {{ VISIT_DETAIL_TEXT.TENANT_CONTINUE_RESCHEDULE }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="isBestPractices"
          class="sm:col-span-2"
          icon="i-lucide-check"
          @click="emit('close')"
        >
          {{ VISIT_DETAIL_TEXT.MANAGED_BY_OWNER_MODAL_BUTTON }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
